import { useEffect, useState } from 'react';

export type StudioCollectionOption = { id: string; name: string; slug?: string };

/** Name + collection (pick or create) before saving a template from the designer. */
export function SaveAsTemplateModal({
  defaultName,
  busy,
  error,
  collections,
  collectionsLoading,
  onClose,
  onCreateCollection,
  onSave,
}: {
  defaultName: string;
  busy?: boolean;
  error?: string;
  collections: StudioCollectionOption[];
  collectionsLoading?: boolean;
  onClose: () => void;
  onCreateCollection: (name: string) => Promise<StudioCollectionOption>;
  onSave: (input: { title: string; collectionIds: string[] }) => void | Promise<void>;
}) {
  const [title, setTitle] = useState(defaultName);
  const [collectionId, setCollectionId] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    setTitle(defaultName);
  }, [defaultName]);

  // Prefer first collection when list arrives and nothing selected.
  useEffect(() => {
    if (!collectionId && collections.length === 1) {
      setCollectionId(collections[0]!.id);
    }
  }, [collections, collectionId]);

  async function handleCreate() {
    const name = newName.trim();
    if (!name || creating || busy) return;
    setLocalError('');
    setCreating(true);
    try {
      const created = await onCreateCollection(name);
      setCollectionId(created.id);
      setNewName('');
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'No se pudo crear la colección');
    } finally {
      setCreating(false);
    }
  }

  function submit() {
    const t = title.trim();
    if (!t || busy) return;
    if (!collectionId) {
      setLocalError('Elige o crea una colección.');
      return;
    }
    setLocalError('');
    void onSave({ title: t, collectionIds: [collectionId] });
  }

  const showError = localError || error;
  const canSave = !!title.trim() && !!collectionId && !busy && !creating;

  return (
    <div className="ox-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="ox-save-tpl-title">
      <div className="ox-modal ox-save-tpl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ox-save-tpl-head">
          <h2 id="ox-save-tpl-title" className="ox-save-tpl-title">
            Guardar como plantilla
          </h2>
          <button type="button" className="ox-added-close" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </div>
        <p className="ox-save-tpl-hint">
          Nombre y colección. El margen (50%) y los precios por vista se calculan automáticamente.
        </p>

        <label className="ox-save-tpl-label" htmlFor="ox-save-tpl-name">
          Nombre de la plantilla
        </label>
        <input
          id="ox-save-tpl-name"
          className="ox-save-tpl-input"
          type="text"
          value={title}
          autoFocus
          disabled={busy}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />

        <label className="ox-save-tpl-label" htmlFor="ox-save-tpl-collection">
          Colección
        </label>
        {collectionsLoading ? (
          <p className="ox-save-tpl-hint" style={{ marginTop: 0 }}>
            Cargando colecciones…
          </p>
        ) : (
          <select
            id="ox-save-tpl-collection"
            className="ox-save-tpl-input ox-save-tpl-select"
            value={collectionId}
            disabled={busy || creating}
            onChange={(e) => setCollectionId(e.target.value)}
          >
            <option value="">Selecciona una colección</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        <div className="ox-save-tpl-new-col">
          <input
            type="text"
            className="ox-save-tpl-input"
            placeholder="O crea una nueva colección"
            value={newName}
            disabled={busy || creating}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleCreate();
              }
            }}
          />
          <button
            type="button"
            className="ox-secondary-btn"
            disabled={!newName.trim() || busy || creating}
            onClick={() => void handleCreate()}
          >
            {creating ? '…' : 'Crear'}
          </button>
        </div>

        {showError ? <p className="ox-save-tpl-error">{showError}</p> : null}
        <div className="ox-save-tpl-actions">
          <button type="button" className="ox-secondary-btn" disabled={busy} onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="ox-primary" disabled={!canSave} onClick={submit}>
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
